import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'

import { normalizeApiError } from '@/lib/apiEnvelope'
import { queryClient } from '@/lib/queryClient'

import { atendimentoApi } from '../api/atendimentoApi'
import type {
  RespostaPayload,
  SolicitacaoDetalhe,
  SolicitacaoPayload,
  SolicitacoesParams,
} from '../types/atendimento'

const LOOKUP_STALE_TIME = 30 * 60 * 1000

export const atendimentoKeys = {
  all: ['atendimento'] as const,
  setores: () => [...atendimentoKeys.all, 'setores'] as const,
  status: () => [...atendimentoKeys.all, 'status'] as const,
  listas: () => [...atendimentoKeys.all, 'lista'] as const,
  abertas: (params: SolicitacoesParams) =>
    [...atendimentoKeys.listas(), 'abertas', params] as const,
  recebidas: (params: SolicitacoesParams) =>
    [...atendimentoKeys.listas(), 'recebidas', params] as const,
  detalhe: (id: string) => [...atendimentoKeys.all, 'detalhe', id] as const,
  historico: (id: string) => [...atendimentoKeys.all, 'historico', id] as const,
}

export function useSetoresAtendimento() {
  return useQuery({
    queryKey: atendimentoKeys.setores(),
    queryFn: atendimentoApi.setores,
    staleTime: LOOKUP_STALE_TIME,
  })
}

export function useStatusAtendimento() {
  return useQuery({
    queryKey: atendimentoKeys.status(),
    queryFn: atendimentoApi.status,
    staleTime: LOOKUP_STALE_TIME,
  })
}

export function useSolicitacoesAbertas(params: SolicitacoesParams, enabled = true) {
  return useQuery({
    queryKey: atendimentoKeys.abertas(params),
    queryFn: () => atendimentoApi.listarAbertas(params),
    placeholderData: keepPreviousData,
    enabled,
  })
}

export function useSolicitacoesRecebidas(params: SolicitacoesParams, enabled = true) {
  return useQuery({
    queryKey: atendimentoKeys.recebidas(params),
    queryFn: () => atendimentoApi.listarRecebidas(params),
    placeholderData: keepPreviousData,
    enabled,
  })
}

export function useSolicitacao(id: string) {
  return useQuery({
    queryKey: atendimentoKeys.detalhe(id),
    queryFn: () => atendimentoApi.obter(id),
    enabled: !!id,
  })
}

export function useHistoricoSolicitacao(id: string) {
  return useQuery({
    queryKey: atendimentoKeys.historico(id),
    queryFn: () => atendimentoApi.historico(id),
    enabled: !!id,
  })
}

function onSolicitacaoAtualizada(solicitacao: SolicitacaoDetalhe) {
  queryClient.setQueryData(atendimentoKeys.detalhe(solicitacao.id), solicitacao)
  void queryClient.invalidateQueries({ queryKey: atendimentoKeys.listas() })
  void queryClient.invalidateQueries({ queryKey: atendimentoKeys.historico(solicitacao.id) })
}

/** Erro exibido no próprio formulário — sem toast. */
export function useCriarSolicitacao() {
  return useMutation({
    mutationFn: (payload: SolicitacaoPayload) => atendimentoApi.criar(payload),
    onSuccess: onSolicitacaoAtualizada,
  })
}

/**
 * Erro exibido no próprio formulário — sem toast. No `409` (status final ou
 * resposta concorrente) recarrega o detalhe para a tela refletir o estado real.
 */
export function useResponderSolicitacao(id: string) {
  return useMutation({
    mutationFn: (payload: RespostaPayload) => atendimentoApi.responder(id, payload),
    onSuccess: onSolicitacaoAtualizada,
    onError: (error) => {
      if (normalizeApiError(error).status === 409) {
        void queryClient.invalidateQueries({ queryKey: atendimentoKeys.detalhe(id) })
      }
    },
  })
}
